/**
 * AiProse.js — Render NaLa2BPMN structured prose with lightweight visual cues.
 *
 * The "prose" is what ``ProseReformulator`` (DSPy) produces and what
 * ``BPMNExtractor`` was trained on. Atlas surfaces it directly as the editable
 * artifact so user edits travel verbatim into DSPy (see Camino 2 plan).
 *
 * The renderer is intentionally shallow — it does NOT parse a grammar. It
 * highlights signals the user already learned to read in NaLa2BPMN:
 *
 *   - Start markers: "El proceso inicia cuando…", "The process starts when…"
 *   - End markers:   "El proceso termina cuando…", "process ends…"
 *   - Decisions:     "Si …, …. Si no, …" / "If …, … . Otherwise, …"
 *   - Parallels:     "simultáneamente", "en paralelo", "in parallel"
 *   - Waits:         "espera recibir", "waits to receive"
 *   - Actors:        capitalised role tokens, optionally seeded from
 *                    ``detected_elements.actors`` returned by /describe.
 *
 * Public API:
 *   - renderProseHtml(text, container, opts)
 */

const RE_START = /\b(?:El proceso inicia cuando|The process starts when|Process starts when)\b[^.]*\./giu;
const RE_END   = /\b(?:El proceso termina(?:\s+cuando)?|The process ends(?:\s+when)?|process ends|process terminates)\b[^.]*\./giu;
const RE_IF    = /\b(?:Si\s+no(?:,)?|Si\b|Si,|Otherwise(?:,)?|If\b)/giu;
const RE_PAR   = /\b(?:simult[áa]neamente|en paralelo|in parallel|concurrently|en\s+forma\s+paralela)\b/giu;
const RE_WAIT  = /\b(?:espera(?:\s+a)?\s+recibir|waits?\s+to\s+receive|waits?\s+for|aguarda(?:r)?\s+recibir)\b/giu;
const RE_TIMER = /\b(?:timer|temporizador|timeout|SLA)\b[^.,]*/giu;

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeRegex(str) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Split prose into sentences using simple heuristics (period followed by space
 * + capital, or end-of-string). Keeps trailing period attached.
 */
function splitSentences(text) {
  if (!text) return [];
  const out = [];
  const re = /[^.!?]+[.!?]+|\S[^.!?]*$/gu;
  let m;
  while ((m = re.exec(text)) !== null) {
    const s = m[0].trim();
    if (s) out.push({ raw: s, start: m.index, end: m.index + m[0].length });
  }
  return out;
}

function classifySentence(sent) {
  const lower = sent.toLowerCase();
  if (RE_START.test(sent)) { RE_START.lastIndex = 0; return 'start'; }
  if (RE_END.test(sent))   { RE_END.lastIndex   = 0; return 'end'; }
  // Reset state in case of /g flags
  RE_START.lastIndex = 0; RE_END.lastIndex = 0;
  if (lower.startsWith('si no') || lower.startsWith('otherwise') ||
      /^\s*(?:si\b|if\b)/.test(lower)) return 'decision';
  if (RE_PAR.test(sent))  { RE_PAR.lastIndex = 0; return 'parallel'; }
  if (RE_WAIT.test(sent)) { RE_WAIT.lastIndex = 0; return 'wait'; }
  RE_PAR.lastIndex = 0; RE_WAIT.lastIndex = 0;
  return 'task';
}

/**
 * Highlight inline tokens (actors, decision keywords, parallel markers) inside
 * a single sentence's text. Returns escaped HTML.
 */
function highlightInline(sentence, actors) {
  let html = escapeHtml(sentence);

  // Actors — replace whole-word matches with chip pills. Sort by length so the
  // longest matches first (avoids "Jefe" eating "Jefe directo").
  if (Array.isArray(actors) && actors.length) {
    const sorted = [...actors].sort((a, b) => (b?.length || 0) - (a?.length || 0));
    for (const actor of sorted) {
      if (!actor || typeof actor !== 'string') continue;
      const re = new RegExp(`\\b(${escapeRegex(actor)})\\b`, 'giu');
      html = html.replace(re, '<span class="atlas-pr__actor">$1</span>');
    }
  }

  // Decision keywords (Si / Si no / If / Otherwise) at start of fragment
  html = html.replace(/^(\s*)(Si\s+no|Si\b|Otherwise|If\b)/u,
    '$1<span class="atlas-pr__kw atlas-pr__kw--decision">$2</span>');

  // Parallel markers
  html = html.replace(/\b(simult[áa]neamente|en paralelo|in parallel|concurrently)\b/giu,
    '<span class="atlas-pr__kw atlas-pr__kw--parallel">$1</span>');

  // Wait markers
  html = html.replace(/\b(espera(?:\s+a)?\s+recibir|waits?\s+to\s+receive|aguarda(?:r)?\s+recibir)\b/giu,
    '<span class="atlas-pr__kw atlas-pr__kw--wait">$1</span>');

  // Start / end phrase fragments
  html = html.replace(/\b(El proceso inicia cuando|The process starts when|Process starts when)\b/gu,
    '<span class="atlas-pr__kw atlas-pr__kw--start">$1</span>');
  html = html.replace(/\b(El proceso termina(?:\s+cuando)?|The process ends(?:\s+when)?)\b/gu,
    '<span class="atlas-pr__kw atlas-pr__kw--end">$1</span>');

  return html;
}

const SYM = {
  start:    '▶',
  end:      '⏹',
  decision: '◆',
  parallel: '▥',
  wait:     '⏱',
  task:     '▣',
};

/**
 * Render the prose into ``container`` as a list of classified sentences.
 *
 * @param {string} text
 * @param {HTMLElement} container
 * @param {{actors?: string[]}} [opts]
 */
export function renderProseHtml(text, container, opts = {}) {
  if (!container) return;
  container.innerHTML = '';

  const trimmed = (text || '').trim();
  if (!trimmed) {
    const empty = document.createElement('div');
    empty.className = 'atlas-pr atlas-pr--empty';
    empty.textContent = '(sin contenido)';
    container.appendChild(empty);
    return;
  }

  const sentences = splitSentences(trimmed);
  const actors = (opts && Array.isArray(opts.actors)) ? opts.actors : [];

  const maxIdx = sentences.length;
  const linenoWidth = String(maxIdx).length;

  sentences.forEach((s, i) => {
    const kind = classifySentence(s.raw);
    const row = document.createElement('div');
    row.className = `atlas-pr atlas-pr--${kind}`;
    row.dataset.idx = String(i + 1);

    const sym = SYM[kind] || SYM.task;
    const inner = highlightInline(s.raw, actors);
    row.innerHTML =
      `<span class="atlas-pr__lineno" aria-hidden="true" style="min-width:${linenoWidth + 1}ch">${i + 1}</span>` +
      `<span class="atlas-pr__sym" aria-hidden="true">${sym}</span>` +
      `<span class="atlas-pr__text">${inner}</span>`;
    container.appendChild(row);
  });
}

/**
 * Lightweight validator for prose. Returns issues; never blocks generation.
 * Real semantics are decided by DSPy server-side — this is just signal for
 * the user that "the structure looks unusual".
 *
 * @param {string} text
 * @returns {Array<{code: string, message: string, severity: 'warning'|'info'}>}
 */
export function validateProse(text) {
  const issues = [];
  const t = (text || '').trim();
  if (!t) {
    issues.push({ code: 'empty', message: 'La descripción está vacía.', severity: 'warning' });
    return issues;
  }
  if (!RE_START.test(t)) {
    issues.push({
      code: 'no_start_marker',
      message: 'Falta una frase de inicio tipo "El proceso inicia cuando…".',
      severity: 'info',
    });
  }
  RE_START.lastIndex = 0;
  if (!RE_END.test(t)) {
    issues.push({
      code: 'no_end_marker',
      message: 'Falta una frase de fin tipo "El proceso termina cuando…".',
      severity: 'info',
    });
  }
  RE_END.lastIndex = 0;
  return issues;
}
